type Trend = "rising" | "falling" | "steady";

function computeTrend(values: number[]): { trend: Trend; delta: number } {
  if (values.length < 3) return { trend: "steady", delta: 0 };

  const recent = values.slice(-3);
  const prior = values.slice(-Math.min(values.length, 7), -3);
  if (prior.length === 0) return { trend: "steady", delta: 0 };

  const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
  const priorAvg = prior.reduce((a, b) => a + b, 0) / prior.length;
  const delta = Math.round((recentAvg - priorAvg) * 10) / 10;

  if (delta >= 0.5) return { trend: "rising", delta };
  if (delta <= -0.5) return { trend: "falling", delta };
  return { trend: "steady", delta };
}

const TREND_CONFIG: Record<
  Trend,
  { label: string; color: string; arrow: string }
> = {
  rising: {
    label: "Improving",
    color: "text-emerald-600",
    arrow: "M4 10l4-4 4 4",
  },
  falling: {
    label: "Declining",
    color: "text-red-500",
    arrow: "M4 6l4 4 4-4",
  },
  steady: {
    label: "Steady",
    color: "text-text-muted",
    arrow: "M3 8h10",
  },
};

export function TrendIndicator({ values }: { values: number[] }) {
  const { trend, delta } = computeTrend(values);

  if (values.length < 3) {
    return (
      <div className="flex items-center gap-1 text-[11px] text-text-muted/50 w-[80px]">
        <span>No trend</span>
      </div>
    );
  }

  const { label, color, arrow } = TREND_CONFIG[trend];

  return (
    <div
      className={`flex items-center gap-1 ${color} w-[80px]`}
      aria-label={`7-day trend: ${label} (${delta >= 0 ? "+" : ""}${delta.toFixed(1)})`}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d={arrow}
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="text-[11px] font-medium leading-none">{label}</span>
    </div>
  );
}
